import React, {useEffect, useMemo, useState} from "react";
import {FormGroup} from "react-bootstrap";
import MethodService from "../../../../services/method.service";
import ExerciseService from "../../../../services/exercise.service";
import {Exercise, ExerciseAttachments, Method, PlanExercise, Stage, UploadAttachment} from "../../../../types/domain";
import {FormWrapper} from "./form/FormWrapper";
import CustomModal from "./modal/CustomModal";
import {ActionIcon, Autocomplete, Button, Checkbox, Group, List, MultiSelect, Paper, Select, Tabs} from "@mantine/core";
import {IconTrash} from "@tabler/icons-react";
import {ModalType} from "../../../../types/UITypes";
import {UploadModal} from "./modal/UploadModal";
import {ExerciseTable} from "./table/ExerciseTable";
import {getTotalAttachmentSize} from "./helperFunctions";
import {MAX_UPLOAD_SIZE_BYTES} from "../../../../types/constants";
import TopicService from "../../../../services/topic.service";

/**
 * @author KMU
 * Helper class to define the panel with the dropdowns
 * to create a new exercise for a plan
 */

type ExercisesData = {
    newExercises: Array<PlanExercise>,
    allExercises: Array<Exercise>,
    userExercises: Array<Exercise>,
    newAttachments: Array<ExerciseAttachments>,
    stages: Array<Stage>,
    dbStages: Array<Stage>
}

//parent property that will be used in this element
type ExercisesFormProps = ExercisesData & {
    updateFields: (fields: Partial<ExercisesData>) => void,
    allMethods: Array<Method>
}

export function ExerciseCreatePanel({
                                        newExercises, allExercises, userExercises,
                                        newAttachments, updateFields,
                                        allMethods, stages, dbStages
                                    }: ExercisesFormProps) {

    //current exercise that is created
    const [exercise, setExercise] = useState({
        name: "",
        description: "",
        material: "",
        methods: [] as string[],
        attachments: []
    });

    const [allTopics, setAllTopics] = useState([{label: "", value: ""}]);
    const [selectedTopic, setSelectedTopic] = useState<string|null>(null);

    // uploaded attachments for the current exercise
    const [attachments, setAttachments] = useState<Array<UploadAttachment>>([]);

    //all methods from database
    const [methods, setMethods] = useState([{label: "", value: ""}]);

    //all exercises from database
    const [exercises, setExercises] = useState({
        names: [{label: "", value: ""}],
        descriptions: [{label: "", value: ""}],
        materials: [{label: "", value: ""}]
    });

    const [selExerciseStages, setSelExerciseStages] = useState<string[]>([]);
    const planStageNames = stages.map(s => s.name);

    const [modal, setModal] = useState<ModalType>({
        show: false,
        title: "",
        body: "",
        decline: false,
        handleOk: () => setModal({...modal, show: false}),
        handleCancel: () => setModal({...modal, show: false})
    });

    const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

    const topicExercises = useMemo(() => {
        if (selectedTopic !== null && selectedTopic !== "" ) {
            return allExercises.filter(exercise =>
                exercise.topics.some(
                    t => t.name.toUpperCase() === selectedTopic.toUpperCase()
                ) ||
                exercise.attachments.some(
                    attachment => attachment.topic.name.toUpperCase() === selectedTopic.toString()
                )
            );
        }
        return allExercises;
    }, [allExercises, selectedTopic]);

    /**
     *  Load data before showing the form
     */
    useEffect(() => {
        TopicService.getAll()
            .then((response: any) => {
                let topics = []
                topics.push({label: "KEIN THEMA", value: ""})
                for (let i in response.data){
                    topics.push({label: response.data[i].name, value: response.data[i].name})
                }
                setAllTopics(topics);
            })
            .catch((e: Error) => {
                console.log(e);
            });
        MethodService.getAll().then((response: any) => {
            let methods = []
            for (let i in response.data) {
                methods.push({label: response.data[i].code, value: response.data[i].code})
            }
            setMethods(methods);
        }).catch((e: Error) => {
            console.log(e);
        });
    }, [])

    /**
     * Reset exercise stage selection when plan stages change or panel resets
     */
    useEffect(() => {
        setSelExerciseStages(stages.map(s => s.name));
    }, [stages]);

    /**
     * Change event of the method dropdown
     * Changing the method leads to a load of the
     * names of all exercises
     * @param newValues newly selected method codes
     */
    async function methodChangeEvent(newValues: string[]) {
        if (newValues === null) {
            //reset current exercise and exercises list
            setExercise({...exercise, methods: []});
            setExercises({...exercises, names: [], descriptions: [], materials: []});
        } else {
            setExercise({...exercise, methods: newValues});

            //get names of exercises belonging to selected method
            let exerciseNames: { label: string; value: string }[] = [];

            // Wait for all backend calls to complete
            const responses = await Promise.all(
                newValues.map(value =>
                    ExerciseService.getNamesByMethod(value)
                        .then((response: any) => response.data)
                        .catch((e: Error) => {
                            console.log(e);
                            return [];
                        })
                )
            );

            responses.flat().forEach((name: string) => {
                exerciseNames.push({ label: name, value: name });
            });
            setExercises({...exercises, names: exerciseNames, descriptions: [], materials: []});
        }
        setAttachments([]);
    }

    /**
     * Change event of the exercise name dropdown
     * Changing the exercise name leads to a load of the
     * descriptions of all exercises with the selected name
     * @param newValue newly selected exercise name
     */
    function nameChangeEvent(newValue: any) {
        if (newValue === null) {
            //reset current exercise and exercises descriptions and materials
            setExercise({...exercise, name: ""});
            setExercises({...exercises, descriptions: [], materials: []});
        } else {
            setExercise({...exercise, name: newValue});

            //get all descriptions of exercises with selected name
            ExerciseService.getAllDesc(newValue)
                .then((response: any) => {
                    let exerciseDescs = []
                    for (let i in response.data) {
                        exerciseDescs.push({label: response.data[i], value: response.data[i]})
                    }
                    setExercises({...exercises, descriptions: exerciseDescs, materials: []});
                })
                .catch((e: Error) => {
                    console.log(e);
                });
        }
    }

    /**
     * Change event of the exercise description dropdown
     * Changing the exercise description leads to a load of the
     * materials of all exercises with the selected name and description
     * @param newValue newly selected exercise description
     */
    function descChangeEvent(newValue: any) {
        if (newValue === null) {
            //reset current exercise description and material and all exercises materials
            setExercise({...exercise, description: ""})
            setExercises({...exercises, materials: []});
        } else {
            setExercise({...exercise, description: newValue})
            ExerciseService.getAllMaterial(exercise.name, newValue)
                .then((response: any) => {
                    let exerciseMaterials = []
                    for (let i in response.data) {
                        exerciseMaterials.push({label: response.data[i], value: response.data[i]})
                    }
                    setExercises({...exercises, materials: exerciseMaterials});
                })
                .catch((e: Error) => {
                    console.log(e);
                });
        }
    }

    /**
     * Change event of the exercise material dropdown
     * @param newValue newly selected exercise material
     */
    function materialChangeEvent(newValue: any) {
        setExercise({...exercise, material: newValue ?? ""});
    }

    function showExerciseExistsModal(){
        setModal({
            ...modal,
            show: true,
            title: "Übung existiert bereits",
            body: "Diese Übung existiert bereits in der Datenbank. " +
                "Bitte wählen Sie diese in der Tabelle aus und fügen Sie Anhänge über den Bearbeiten-Button (✏️) hinzu.",
            handleOk: () => setModal({...modal, show: false}),
            handleCancel: () => setModal({...modal, show: false})
        });
    }

    /**
     * Add current exercise to the list of new exercises
     */
    async function addExercise() {
        if (exercise.methods.length == 0) {
            setModal({...modal, body: "Es muss eine Methode ausgewählt werden!", show: true});
        } else if (exercise.name === "") {
            setModal({...modal, body: "Es muss ein Übungsname vergeben werden!", show: true});
        } else if (exerciseAlreadyExists()) {
            showExerciseExistsModal();
        } else if (newExercises.some(e => e.exercise.name === exercise.name
            && e.exercise.description === exercise.description && e.exercise.material === exercise.material)) { //check if exercise not already in list
            setModal({...modal, body: "Diese Tätigkeit befindet sich bereits in der Liste!", show: true});
        } else {
            await addExeToNewExes(exercise.name, exercise.description, exercise.material,
                exercise.methods, selExerciseStages);

            //reset current exercise and exercise list
            setExercise({
                description: "", material: "",
                name: "", methods: [], attachments: []
            })
            setExercises({...exercises, names: [], descriptions: [], materials: []});
            setAttachments([]);
            setSelExerciseStages(planStageNames);
        }
    }

    /**
     * Helper function to add an exercise to the list of new exercises
     * @param name exercise name
     * @param description exercise description
     * @param material exercise material
     * @param methods exercise methods
     * @param selectedStageNames names of the selected stages
     */
    async function addExeToNewExes(name: string, description: string, material: string,
                                   methods: string[], selectedStageNames: string[]) {
        let dbMethods = []
        for (const method of methods) {
            const dbMethod = allMethods.find(m => m.code === method.trim())
            if (dbMethod) {
                dbMethods.push(dbMethod)
            }
        }

        if (dbMethods.length !== 0) {
            //check if exercise already in database
            let dbExercise = (await ExerciseService.getByNameDescMat(name, description, material)).data;
            if (typeof (dbExercise) === 'string') {   //not in db
                dbExercise = {
                    id: 0,
                    name: name,
                    description: description,
                    material: material,
                    methods: dbMethods,
                    stages: dbStages.filter(s => selectedStageNames.includes(s.name)),  // use selected
                    topics: [],
                    attachments: [],
                    user: undefined,
                    parentId: undefined
                }
            } else {
                if (dbExercise.methods.length !== methods.length ||           //the methods are different to before
                    !dbExercise.methods
                        .every((value, index) => value.name === methods[index])){
                    dbMethods.forEach(method => {
                        if (!dbExercise.methods.includes(method)){
                            dbExercise.methods.push(method);
                        }
                    });
                }

                // Merge selected stages without duplicates
                const newStages = dbStages.filter(s =>
                    selectedStageNames.includes(s.name) &&
                    !dbExercise.stages.some((es: Stage) => es.name === s.name)
                );
                dbExercise.stages = [...dbExercise.stages, ...newStages];
            }

            //update list with new attachments
            if (attachments.length !== 0) {
                newAttachments.push({
                    exercise: dbExercise,
                    attachments: attachments
                });
            }

            //update list with new exercises
            let adaptNewExe = [...newExercises];
            let newPlanExercise: PlanExercise = {
                id: 0,
                exercise: dbExercise,
                attachments: [],
                execution: undefined,
                interest: undefined,
                position: adaptNewExe.length+1
            }
            adaptNewExe.push(newPlanExercise);


            updateFields({newExercises: adaptNewExe, newAttachments: newAttachments})
        }
    }

    /**
     * Delete Attachment
     * @param a - attachment to delete
     */
    function deleteAttachment(a: UploadAttachment) {
        setAttachments(attachments.filter(ua => ua.type.name !== a.type.name || ua.name !== a.name));
    }

    /**
     * Check if the currently entered exercise already exists in the database.
     * Such exercises must be added via the table + edit button.
     */
    function exerciseAlreadyExists(): boolean {
        if (exercise.name === "") return false;
        return allExercises.some(e =>
            e.name === exercise.name &&
            e.description === exercise.description &&
            e.material === exercise.material
        );
    }

    /**
     * Check if the limit of the max upload size is not reached yet.
     * If so then ask user to save first before any further uploads.
     */
    function handleOpenUploadModal(){
        if (exerciseAlreadyExists()) {
            showExerciseExistsModal();
            return;
        }

        const currentSize = getTotalAttachmentSize(newAttachments);
        if (currentSize >= MAX_UPLOAD_SIZE_BYTES) {
            setModal({
                ...modal,
                show: true,
                title: "Upload-Limit erreicht",
                body: "Die maximale Upload-Größe von 20MB wurde erreicht. Bitte speichern Sie den Plan zuerst, bevor Sie weitere Anhänge hinzufügen.",
                handleOk: () => setModal({...modal, show: false}),
                handleCancel: () => setModal({...modal, show: false})
            });
            return;
        }
        setShowUploadModal(true);
    }

    return (
        <>
            <FormWrapper title="Tätigkeitstabelle">
                <Tabs defaultValue="all">
                    <Tabs.List>
                        <Tabs.Tab value="all">Alle Übungen</Tabs.Tab>
                        <Tabs.Tab value="own">Eigene Übungen</Tabs.Tab>
                        <Tabs.Tab value="new">Neue Übung erstellen</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value="all">
                        <Select
                            data={allTopics}
                            onChange={value => {setSelectedTopic(value)}}
                            placeholder={"KEIN THEMA"}
                            searchable
                            clearable
                            label="Themenfilter: "
                            style={{ maxWidth: '30%' }}
                        />
                        <ExerciseTable newExercises={newExercises} exercises={topicExercises}
                                       allMethods={allMethods}
                                       showErrorModal={(error: string) => {
                                           setModal({...modal, body: error, show: true});
                                       }}
                                       showOwnExercisesFirstButton={true}
                                       updateFields={updateFields}/>
                    </Tabs.Panel>
                    <Tabs.Panel value="own">
                        <ExerciseTable newExercises={newExercises} exercises={userExercises}
                                       allMethods={allMethods}
                                       showErrorModal={(error: string) => {
                                           setModal({...modal, body: error, show: true});
                                       }}
                                       showOwnExercisesFirstButton={false}
                                       updateFields={updateFields}/>
                    </Tabs.Panel>
                    <Tabs.Panel value="new">
                        <Paper shadow="xs" radius="xs" p="md" withBorder style={{marginTop: "1rem"}}>
                            <FormGroup className="newExerciseField">
                                <MultiSelect
                                    data={methods}
                                    onChange={methodChangeEvent}
                                    value={exercise.methods}
                                    placeholder="Wähle eine Fördermethode aus"
                                    clearable
                                    label="Methode"
                                    withAsterisk
                                />
                            </FormGroup>
                            <FormGroup className="newExerciseField">
                                <Autocomplete
                                    data={exercises.names}
                                    onChange={nameChangeEvent}
                                    value={exercise.name}
                                    placeholder="Wähle eine Übung aus"
                                    label="Übung/Tätigkeit"
                                    limit={exercises.names.length}
                                    withAsterisk
                                    styles={{dropdown: {maxHeight: 200, overflow: 'auto'}}}
                                />
                            </FormGroup>

                            <FormGroup className="newExerciseField">
                                <Autocomplete
                                    data={exercises.descriptions}
                                    onChange={descChangeEvent}
                                    value={exercise.description}
                                    placeholder="Wähle eine Beschreibung aus"
                                    label="Beschreibung"
                                    limit={exercises.descriptions.length}
                                    styles={{dropdown: {maxHeight: 200, overflow: 'auto'}}}
                                />
                            </FormGroup>

                            <FormGroup className="newExerciseField">
                                <Autocomplete
                                    data={exercises.materials}
                                    onChange={materialChangeEvent}
                                    value={exercise.material}
                                    placeholder="Wähle ein Material aus"
                                    label="Material"
                                    limit={exercises.materials.length}
                                    styles={{dropdown: {maxHeight: 200, overflow: 'auto'}}}
                                />
                            </FormGroup>

                            <FormGroup className="newExerciseField">
                                <Checkbox.Group
                                    value={selExerciseStages}
                                    onChange={setSelExerciseStages}
                                    label="Geeignete Stadien"
                                    withAsterisk
                                >
                                    <Group>
                                        {dbStages.map(stage => (
                                            <Checkbox
                                                key={stage.name}
                                                label={stage.name}
                                                value={stage.name}
                                            />
                                        ))}
                                    </Group>
                                </Checkbox.Group>
                            </FormGroup>

                            <Button onClick={handleOpenUploadModal}>
                                Anhang hinzufügen
                            </Button>

                            {attachments.length !== 0 ?
                                <div style={{marginTop: "1rem"}}>
                                    <h6>Hinzugefügte Anhänge</h6>
                                    <List>
                                        {attachments.map((a, index) =>
                                            <List.Item key={index}>
                                                <div style={{display: "flex", alignItems: "center", gap: "0.5rem"}}>
                                                    {a.type?.name}: {a.name}
                                                    {a.solutionFile && ` | Lösungsblatt: ${a.solutionFile.name}`}
                                                    <ActionIcon color="red" onClick={() => deleteAttachment(a)}>
                                                        <IconTrash/>
                                                    </ActionIcon>
                                                </div>
                                            </List.Item>
                                        )}
                                    </List>
                                </div> : <></>
                            }

                            <hr></hr>

                            <Button id="newExerciseBtn" onClick={addExercise}>
                                Übung hinzufügen
                            </Button>
                        </Paper>
                    </Tabs.Panel>
                </Tabs>
            </FormWrapper>
            <CustomModal title={modal.title} body={modal.body}
                         decline={false} showModal={modal.show} handleOk={modal.handleOk}
                         handleCancel={modal.handleCancel}
            />
            <UploadModal opened={showUploadModal}
                         close={() => setShowUploadModal(false)}
                         attachments={attachments}
                         onAdd={(attachment) => setAttachments(prev => [...prev, attachment])}
                         allStages={dbStages}
                         planStages={stages}/>
        </>
    )
}